import { Flex } from '@primitives/Layout'
import { useLocation } from '@reach/router'
import React, { useEffect } from 'react'
import { Search as SearchIconSVG } from 'react-feather'
import styled from 'styled-components'
import { color } from 'styled-system'

import { SearchInput } from './SearchInput'
import './docsearch.min.css'
import { supportLangFromPath } from '../../../translation/languages'

const SearchIcon = styled(SearchIconSVG)`
  position: absolute;
  left: ${({ left = 'initial' }) => `${left}`};
  right: ${({ right = 'initial' }) => `${right}`};
  top: 50%;
  transform: translate(50%, -50%);
  z-index: 10;
  pointer-events: none;
  ${color};
`

// style algolia
const StyledFlex = styled(Flex)`
  & span.algolia-autocomplete {
    /* ensure full width input on mobile */
    display: block !important;
  }
  .algolia-docsearch-footer {
    display: none;
  }
  .algolia-autocomplete {
    display: block;
    width: 100%;
    height: 100%;
  }
  /* max mobile width */
  @media only screen and (max-width: 768px) {
    & .algolia-autocomplete .ds-dropdown-menu {
      left: -8px !important;
      right: -8px !important;
      min-width: auto;
    }
    & .algolia-autocomplete .algolia-docsearch-suggestion {
      margin-bottom: 8px;
      .algolia-docsearch-suggestion--subcategory-column {
        display: block;
        float: none;
        margin-bottom: 5px;
        &:after {
          content: '';
        }
      }
    }
  }
`

// Development URL flags for local Algolia search
const MASTER_URL = 'https://bonobolabs.com'
const LOCAL_URL = 'http://localhost:8000'

// Docsearch wraps the input in `.algolia-autocomplete`; unwrap before re-init.
const destroyDocsearch = inputSelector => {
  const input = document.querySelector(inputSelector)
  if (!input) return

  const wrapper = input.closest('.algolia-autocomplete')
  if (wrapper?.parentNode) {
    wrapper.parentNode.replaceChild(input, wrapper)
  }
}

// Docsearch (Algolia) is ~170KB. Instead of loading it globally on every page
// (it used to sit in html.js), load it on demand only when a Search input is
// actually visible. The header search is display:none on mobile, so mobile
// never pays for it, and desktop loads it after hydration rather than racing
// the LCP image.
const DOCSEARCH_SRC = '/docsearch.min.js'
let docsearchPromise = null

const loadDocsearch = () => {
  if (typeof window === 'undefined') return Promise.resolve(null)
  if (window.docsearch) return Promise.resolve(window.docsearch)
  if (docsearchPromise) return docsearchPromise

  docsearchPromise = new Promise((resolve, reject) => {
    let script = document.querySelector(`script[src="${DOCSEARCH_SRC}"]`)
    if (!script) {
      script = document.createElement('script')
      script.src = DOCSEARCH_SRC
      script.async = true
      document.body.appendChild(script)
    }
    script.addEventListener('load', () => resolve(window.docsearch))
    script.addEventListener('error', reject)
  })
  return docsearchPromise
}

const initDocsearch = (inputSelector, lang) => {
  window.docsearch({
    apiKey: 'a21ddd72d21a4cb30e3b12b9baab89b1',
    appId: 'WE3OG9WSIH',
    indexName: 'moleskinestudio',
    inputSelector,
    // Set debug to true if you want to inspect the dropdown
    debug: false,
    algoliaOptions: {
      analyticsTags: ['mds-home'],
      // On a translated /support/ page, scope results to that language.
      // Requires the Algolia index to expose a `language` facet — see
      // docsearch_config.json; takes effect after a re-crawl. English and all
      // other pages are unaffected (no filter).
      ...(lang !== 'en' ? { facetFilters: [`language:${lang}`] } : {})
    },
    transformData: hits => {
      return hits.map(hit => {
        // If we're in development, replace the search URL with localhost, this is just a convenience change
        // to speed up development things
        if (process.env.NODE_ENV !== 'development') {
          return hit
        }

        hit.url = hit.url.replace(MASTER_URL, LOCAL_URL)
        return hit
      })
    }
  })

  // https://github.com/algolia/docsearch/issues/418
  document.querySelector(inputSelector)?.setAttribute('aria-label', 'Search')
}

export const Search = ({
  bg: initialBg = 'white',
  inputId = 'search',
  size = 'normal',
  variant = 'normal',
  iconColor: overrideIconColor,
  ...props
}) => {
  const { pathname } = useLocation()
  const lang = supportLangFromPath(pathname)

  useEffect(() => {
    const inputSelector = `#${inputId}`
    const input = document.querySelector(inputSelector)
    if (!input) return

    let cancelled = false
    let initialized = false

    const removeTriggers = () => {
      input.removeEventListener('focus', ensureInit)
      input.removeEventListener('pointerenter', ensureInit)
      window.removeEventListener('resize', ensureInit)
    }

    function ensureInit() {
      if (initialized || cancelled) return
      if (input.getClientRects().length === 0) return
      initialized = true
      removeTriggers()
      loadDocsearch().then(docsearch => {
        if (cancelled || !docsearch) return
        initDocsearch(inputSelector, lang)
      })
    }

    // Visible now (desktop) -> init immediately; otherwise wait for it to show.
    ensureInit()
    if (!initialized) {
      input.addEventListener('focus', ensureInit)
      input.addEventListener('pointerenter', ensureInit)
      window.addEventListener('resize', ensureInit)
    }

    return () => {
      cancelled = true
      removeTriggers()
      if (initialized) destroyDocsearch(inputSelector)
    }
  }, [inputId, lang])

  let bg = initialBg
  const fontSize = { normal: 16, large: 20 }[size]
  const iconSize = { normal: 24, large: 24 }[size]

  const iconPadding = { normal: '42px', large: '50px' }[size]

  let iconLeft = { normal: '0px', large: '4px' }[size]
  let iconRight = { normal: '20px', large: '24px' }[size]

  const py = { normal: 12, large: 19 }[size]
  const borderRadius = { normal: 5, large: 11 }[size]

  let iconColor =
    overrideIconColor || { normal: '#707070', large: '#A7A7A7' }[size]

  const border = '2px solid transparent'

  let pl, pr
  if (variant === 'subtle') {
    bg = 'transparent'
    iconColor = overrideIconColor || 'rgba(255, 255, 255, 0.8)'
    pl = 2
    pr = iconPadding
    iconLeft = 'initial'
  } else {
    pr = 2
    pl = iconPadding
    iconRight = 'initial'
  }

  return (
    <StyledFlex width="100%" {...props}>
      <SearchInput
        id={inputId}
        width="100%"
        py={py}
        pl={pl}
        pr={pr}
        fontSize={fontSize}
        borderRadius={borderRadius}
        border={border}
        bg={bg}
        aria-label="Search"
        {...props}
      />
      <SearchIcon
        color={iconColor}
        size={iconSize}
        left={iconLeft}
        right={iconRight}
      />
    </StyledFlex>
  )
}
